/**
 * Per-ticket pane source (issue #90): a thread with its own `desk_ticket_id` is an external conversation (a contractor,
 * vendor or outside support desk on its own Desk ticket in the Vendors & Contractors department). Its pane is named
 * after the party plus "#<ticket number>", carries a small party-kind marker, and sorts after the case's own panes
 * (customer first, internal last) in the order the tickets were opened. Internal-only labels: these panes never reach
 * a customer renderer.
 *
 * A customer-audience pane is never external (#1004): since #948 the case's Customer pane carries its primary ticket
 * in `desk_ticket_id` too. It always reads "Customer"; when a case shows two party-less Customer panes, each one
 * reads "Customer #<ticket number>" so they can be told apart.
 */

/** The ThreadOut fields this helper reads (ThreadOut gained party_kind, desk_ticket_id and ticket_number). */
export interface PaneThread {
  id: string
  audience?: string | null
  party_ref?: string | null
  display_name_internal?: string | null
  party_kind?: string | null
  desk_ticket_id?: string | null
  ticket_number?: string | number | null
}

/** The pane fields the helper sets (a subset of the case view's ThreadView). */
export interface PaneView {
  id: string
  label: string
  sublabel?: string
  audience: string
}

const KIND_MARKER: Record<string, string> = {
  contractor: 'Contractor',
  vendor: 'Vendor',
  external_support: 'External support',
}

export function isExternalPane(t: PaneThread): boolean {
  return Boolean(t.desk_ticket_id) && t.audience !== 'customer'
}

function ticketNumber(t: PaneThread): string {
  return t.ticket_number === null || t.ticket_number === undefined ? '' : String(t.ticket_number).replace(/^#/, '')
}

/** "Customer", or "Customer #23991" when the case shows more than one party-less Customer pane. */
function labelCustomerPanes<V extends PaneView>(views: V[], plain: readonly PaneThread[]): void {
  const ordinary = plain.filter((t) => t.audience === 'customer' && !t.party_ref && views.some((v) => v.id === t.id))
  if (ordinary.length < 2) return
  for (const t of ordinary) {
    const v = views.find((view) => view.id === t.id)
    if (!v) continue
    const num = ticketNumber(t)
    v.label = num ? `Customer #${num}` : 'Customer'
  }
}

export function partyKindMarker(kind: string | null | undefined): string {
  return (kind && KIND_MARKER[kind]) || 'External'
}

/** "Northside Electric #23991" (the party's internal name, then its own Desk ticket number). */
export function externalPaneLabel(t: PaneThread): string {
  const name = (t.display_name_internal ?? '').trim() || partyKindMarker(t.party_kind)
  const num = ticketNumber(t)
  return num ? `${name} #${num}` : name
}

/**
 * Relabels external-conversation panes in place and moves them after the case's own panes, keeping the internal
 * pane last. Takes ThreadOut rows or the mirror's `{thread, turns}` rows. Panes without a `desk_ticket_id` keep their
 * label and relative order. Returns the same array.
 */
export function applyExternalPanes<V extends PaneView>(views: V[], threads: readonly (PaneThread | { thread: PaneThread })[]): V[] {
  const plain = threads.map((t) => ('thread' in t ? t.thread : t))
  labelCustomerPanes(views, plain)
  const byId = new Map(plain.filter(isExternalPane).map((t) => [t.id, t]))
  if (byId.size === 0) return views
  for (const v of views) {
    const t = byId.get(v.id)
    if (!t) continue
    v.label = externalPaneLabel(t)
    v.sublabel = partyKindMarker(t.party_kind)
  }
  const rank = (v: V) => (v.audience === 'internal' ? 2 : byId.has(v.id) ? 1 : 0)
  const sorted = views.map((v, i) => ({ v, i })).sort((a, b) => rank(a.v) - rank(b.v) || a.i - b.i)
  sorted.forEach(({ v }, i) => {
    views[i] = v
  })
  return views
}

const GENERIC_PANE_NAMES = new Set(['', 'contractor', 'vendor', 'external', 'external support', 'internal', 'customer'])

/** A ThreadView-shaped pane. The case view calls this after external labels are applied. */
export interface LeftoverPane {
  id: string
  label: string
  audience: string
  turns: readonly { draft?: unknown }[]
  composer?: { draftHtml?: string }
  partyAudience?: 'contractor' | 'vendor'
  partyRef?: string
  deskTicketId?: string
}

function paneName(label: string | null | undefined): string | null {
  const text = (label ?? '')
    .replace(/\s+#\d+$/, '')
    .trim()
    .toLowerCase()
  return GENERIC_PANE_NAMES.has(text) ? null : text
}

function paneHasDraft(view: LeftoverPane): boolean {
  if ((view.composer?.draftHtml ?? '').trim()) return true
  return view.turns.some((turn) => Boolean(turn.draft))
}

function partyKind(view: LeftoverPane): string {
  if (view.partyAudience) return view.partyAudience
  return view.audience === 'contractor' ? 'contractor' : ''
}

/**
 * #1037 client filter, defense in depth behind the API. Drops a ticketless turn-less contractor or vendor
 * pane when the same firm already has a ticketed pane, and drops an extra empty Internal pane. A lone empty
 * contractor stays: only the API knows whether a work order or visit still needs that tab. Does not write.
 */
export function omitLeftoverPanes<V extends LeftoverPane>(views: V[]): V[] {
  const ticketed = views.filter((view) => view.deskTicketId && (partyKind(view) === 'contractor' || partyKind(view) === 'vendor'))
  const drop = new Set<string>()
  for (const view of views) {
    const kind = partyKind(view)
    if (kind !== 'contractor' && kind !== 'vendor') continue
    if (view.deskTicketId || view.turns.length > 0 || paneHasDraft(view)) continue
    const ref = (view.partyRef ?? '').trim().toLowerCase()
    const name = paneName(view.label)
    const matched = ticketed.some((other) => {
      if (partyKind(other) !== kind) return false
      const otherRef = (other.partyRef ?? '').trim().toLowerCase()
      if (ref && otherRef && ref === otherRef) return true
      const otherName = paneName(other.label)
      return Boolean(name && otherName && name === otherName)
    })
    if (matched) drop.add(view.id)
  }
  const internals = views.filter((view) => view.audience === 'internal')
  const qualified = internals.filter((view) => view.turns.length > 0 || Boolean(view.deskTicketId) || paneHasDraft(view))
  const keep = new Set((qualified.length > 0 ? qualified : internals.slice(0, 1)).map((view) => view.id))
  for (const view of internals) {
    if (!keep.has(view.id)) drop.add(view.id)
  }
  if (drop.size === 0) return views
  return views.filter((view) => !drop.has(view.id))
}
